import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import AiAvatar from "~/ui/shared/assets/ai-avatar.png";
import {
  getHistoryThemes,
  getUserChecklists,
} from "~/data/history/history.service";
// import { getScenariosCategories } from "~/data/dashboard/dashboard.service";

import { Filters } from "./filters";
import { Dialog } from "../shared/dialog";
import { Avatar } from "../shared/avatar";
import { Button } from "../shared/button";
import { Spinner } from "../shared/spinner";
import { InputSearch } from "../shared/input";
import { UserAvatar } from "../shared/user-avatar";
import { useUser } from "../shared/queries/user-query";
import { ChecklistModal } from "../training/checklist-modal";
import { useIntesectionObserver } from "../shared/utils/use-intersection-observer";

export function History() {
  // TODO: create better types
  const user = useUser();
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalFilterOpen, setIsModalFilterOpen] = useState(false);
  const [searchParams] = useSearchParams();

  const selectedCategory = searchParams.get("category_id");

  const categoriesState = useQuery({
    queryKey: ["history_themes"],
    queryFn: getHistoryThemes,
    staleTime: 10 * 60 * 1000, // 10 min.
    select: (rows) =>
      rows.filter((it) =>
        it.name.toLowerCase().includes(searchTerm.toLowerCase())
      ),
  });
  const category = categoriesState.data?.find(
    (it) => it.id === selectedCategory
  );

  const filters = Object.fromEntries(searchParams);
  const { data, isPending, isLoadingError, isFetchingNextPage, fetchNextPage } =
    useInfiniteQuery({
      queryKey: ["checklists", filters],
      queryFn: ({ pageParam }) =>
        getUserChecklists({ ...filters, page: pageParam }),
      initialPageParam: 1,
      getNextPageParam: (last) => last.meta.next_page,
      staleTime: 1 * 60 * 1000, // 1 min.
      select: (state) => ({
        history: state.pages.flatMap((it) => it.data),
        total_count: state.pages[0].meta.total_items,
        next: state.pages[state.pages.length - 1].meta.next_page,
      }),
    });
  const ref = useIntesectionObserver<HTMLDivElement>(() => {
    if (!data?.next || isPending || isFetchingNextPage) return;
    fetchNextPage();
  });

  return (
    <div className="p-10 bg-white">
      <section className="px-8 mb-5 flex justify-between items-center py-2">
        <div>
          <h3 className="font-alternative text-3xl font-semibold text-[#5B5B5B] mb-1">
            Histórico de Práticas
          </h3>
          <p className="text-[#646464] font-alternative leading-4 text-sm">
            Visualize suas práticas e notas
          </p>
        </div>

        <div className="flex items-center space-x-4">
          <Button variant="primary-1" className="h-10 w-60" asChild>
            <Link to="/cenarios">Começar nova Prática</Link>
          </Button>
          <InputSearch
            className="relative w-64"
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Busque por categoria"
          />
        </div>
      </section>

      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6 p-5 mb-5">
        {categoriesState.isPending ? (
          <div className="text-center py-8">Carregando categorias...</div>
        ) : categoriesState.isLoadingError ? (
          <div className="text-center py-8">
            Erro ao carregar as categorias.
          </div>
        ) : (
          categoriesState.data.map((category) => {
            const search = new URLSearchParams(searchParams);
            search.set("category_id", category.id);
            return (
              <Link
                key={category.id}
                className="flex px-4 py-5 rounded-[20px] h-[150px] border-l-[6px] border-l-alternative-2 bg-[#f9f9f9] bg-cover bg-center bg-no-repeat"
                style={{
                  backgroundImage:
                    selectedCategory === category.id
                      ? `linear-gradient(90deg, rgba(0, 0, 0, 0.80) 0%, rgba(0, 0, 0, 0.00) 100%), url('${
                          import.meta.env.VITE_API_URL + category.banner_url
                        }')`
                      : undefined,
                }}
                to={{ search: search.toString() }}
                onMouseEnter={() => {
                  // preloads banner image on hover
                  const img = new Image();
                  img.src = import.meta.env.VITE_API_URL + category.banner_url;
                }}
              >
                <div className="flex flex-col items-start">
                  <h4 className="text-alternative-1 font-semibold text-sm uppercase">
                    {category.name}
                  </h4>
                  <p
                    className={`${
                      selectedCategory === category.id
                        ? "text-white"
                        : "text-gray-600"
                    }`}
                  >
                    <span
                      className={`font-normal text-xs ${
                        selectedCategory === category.id
                          ? "text-white"
                          : "text-black"
                      }`}
                    >
                      <span className="font-semibold">
                        {category.user_tests_count}
                      </span>{" "}
                      {category.user_tests_count > 1
                        ? "Práticas realizadas"
                        : "Prática realizada"}
                    </span>
                  </p>
                </div>
              </Link>
            );
          })
        )}
      </section>

      <section className="mb-6">
        <div className="flex justify-between items-center px-8 py-2 mb-5">
          <h4 className="text-2xl font-semibold font-alternative text-[#5B5B5B]">
            {category?.name ?? "Todos"}
            <span className="text-base text-[#2D2D2D] ml-2">
              ({category?.user_tests_count ?? data?.total_count ?? 0})
            </span>
          </h4>
          <button
            type="button"
            onClick={() => setIsModalFilterOpen(true)}
            aria-label="Abrir Filtros"
          >
            <svg
              width="32"
              height="32"
              viewBox="0 0 32 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M14.8189 15.3684L21.6258 6.04632C21.8177 5.78299 21.9043 5.44917 21.8667 5.11781C21.829 4.78644 21.6702 4.48448 21.4249 4.27789C21.2266 4.10743 20.982 4.00996 20.7277 4H4.18307C3.91651 4.00165 3.6583 4.09959 3.45038 4.27789C3.20509 4.48448 3.04627 4.78644 3.00862 5.11781C2.97098 5.44917 3.05757 5.78299 3.24948 6.04632L10.0919 15.3684V22.7832C10.0705 22.9743 10.0902 23.1681 10.1496 23.3498C10.209 23.5315 10.3065 23.6963 10.4346 23.8316L12.7981 26.3579C13.0195 26.5932 13.319 26.7252 13.6312 26.7252C13.9434 26.7252 14.243 26.5932 14.4644 26.3579C14.5946 26.2238 14.6942 26.0595 14.7557 25.8777C14.8172 25.696 14.8388 25.5016 14.8189 25.3095V15.3684ZM17.1824 21.6842L23.0912 28L29 21.6842H17.1824Z"
                fill="#393939"
              />
            </svg>
          </button>
        </div>

        <div className="grid gap-[10px]">
          <div className="bg-white rounded-[10px] shadow-card flex py-5 px-10 font-alternative text-lg font-medium leading-6">
            <span>Equipe</span>
            <span className="ml-32">Cenário</span>
            <span className="ml-auto">Média Geral</span>
          </div>

          {isPending ? (
            <div className="text-center py-8">Carregando...</div>
          ) : isLoadingError ? (
            <div className="text-center py-8">
              Erro ao carregar as práticas.
            </div>
          ) : (
            <>
              {data.history.map((item) => {
                const score = item.final_score ?? 0;

                const formattedDate = item.finished_at
                  ? new Date(item.finished_at).toLocaleString([], {
                      dateStyle: "short",
                      timeStyle: "short",
                    })
                  : "Não Finalizado";

                const isLowScore = score < 7;
                return (
                  <ChecklistModal
                    key={item.id}
                    title={item.test.title}
                    // @ts-expect-error TODO: type this better
                    questions={item.questions}
                    // @ts-expect-error TODO: type this better
                    answers={item.answers}
                    trigger={
                      <div
                        key={`trigger:${item.id}`}
                        className="flex items-center justify-between bg-white p-6 shadow-card rounded-[10px]"
                      >
                        <div className="flex items-center">
                          <div>
                            <UserAvatar
                              size={96}
                              url={user.avatar_url}
                              name={user.name}
                            />
                            <p className="text-center uppercase tracking-wider text-[8px] text-surface-foreground-variant">
                              {user.name}
                            </p>
                          </div>
                          <div className="-ml-4">
                            {item.patient ? (
                              <UserAvatar
                                size={96}
                                url={item.patient.avatar_url}
                                name={item.patient.name}
                              />
                            ) : (
                              <Avatar size={96} src={AiAvatar} />
                            )}
                            <p className="text-center tracking-wider uppercase text-[8px] text-surface-foreground-variant">
                              {item.patient?.name ?? "IA"}
                            </p>
                          </div>

                          <div className="px-6">
                            <h5 className="text-lg leading-6 font-medium font-alternative text-[#2D2D2D] mb-[10px]">
                              {item.test?.title}
                            </h5>
                            <p className="text-[#8D8D8D] font-medium text-xs leading-[10px]">
                              {formattedDate}
                            </p>
                          </div>
                        </div>

                        <div
                          className={`flex flex-col items-center justify-center gap-1 rounded-lg ${
                            isLowScore ? "bg-[#FA4F3B]" : "bg-alternative-1"
                          }`}
                          style={{ width: 100, height: 100 }}
                        >
                          {isLowScore ? (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="40"
                              height="40"
                              viewBox="0 0 40 40"
                              fill="none"
                            >
                              <path
                                d="M20 3.63636C17.8511 3.63636 15.7232 4.05962 13.7379 4.88197C11.7526 5.70432 9.94867 6.90966 8.42916 8.42916C6.90966 9.94867 5.70432 11.7526 4.88197 13.7379C4.05962 15.7232 3.63636 17.8511 3.63636 20C3.63636 22.1489 4.05962 24.2768 4.88197 26.2621C5.70432 28.2474 6.90966 30.0513 8.42916 31.5708C9.94867 33.0903 11.7526 34.2957 13.7379 35.118C15.7232 35.9404 17.8511 36.3636 20 36.3636C24.3399 36.3636 28.5021 34.6396 31.5708 31.5708C34.6396 28.5021 36.3636 24.3399 36.3636 20C36.3636 15.6601 34.6396 11.4979 31.5708 8.42916C28.5021 5.36038 24.3399 3.63636 20 3.63636ZM0 20C0 8.95455 8.95455 0 20 0C31.0455 0 40 8.95455 40 20C40 31.0455 31.0455 40 20 40C8.95455 40 0 31.0455 0 20ZM10.4891 12.9709L16.7873 16.6073L14.9691 19.7564L8.67091 16.12L10.4891 12.9709ZM31.3327 16.12L25.0345 19.7564L23.2164 16.6073L29.5145 12.9709L31.3327 16.12ZM12.1273 26.3618C12.9251 24.9799 14.0727 23.8325 15.4547 23.0349C16.8367 22.2373 18.4044 21.8177 20 21.8182C21.5958 21.8179 23.1636 22.2378 24.5456 23.0357C25.9276 23.8336 27.0751 24.9814 27.8727 26.3636L28.7836 27.9364L25.6364 29.7564L24.7273 28.1836C24.2492 27.353 23.5603 26.6632 22.7303 26.184C21.9002 25.7048 20.9584 25.4532 20 25.4545C19.0419 25.4539 18.1006 25.7057 17.2707 26.1844C16.4409 26.6632 15.7518 27.3521 15.2727 28.1818L14.3636 29.7564L11.2164 27.9364L12.1273 26.3618Z"
                                fill="#FBFBFB"
                              />
                            </svg>
                          ) : (
                            <svg
                              width="40"
                              height="40"
                              viewBox="0 0 40 40"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M20 3.63636C17.8511 3.63636 15.7232 4.05962 13.7379 4.88197C11.7526 5.70432 9.94867 6.90966 8.42916 8.42916C6.90966 9.94867 5.70432 11.7526 4.88197 13.7379C4.05962 15.7232 3.63636 17.8511 3.63636 20C3.63636 22.1489 4.05962 24.2768 4.88197 26.2621C5.70432 28.2474 6.90966 30.0513 8.42916 31.5708C9.94867 33.0903 11.7526 34.2957 13.7379 35.118C15.7232 35.9404 17.8511 36.3636 20 36.3636C24.3399 36.3636 28.5021 34.6396 31.5708 31.5708C34.6396 28.5021 36.3636 24.3399 36.3636 20C36.3636 15.6601 34.6396 11.4979 31.5708 8.42916C28.5021 5.36038 24.3399 3.63636 20 3.63636ZM0 20C0 8.95455 8.95455 0 20 0C31.0455 0 40 8.95455 40 20C40 31.0455 31.0455 40 20 40C8.95455 40 0 31.0455 0 20ZM14.5455 12.7273V20H10.9091V12.7273H14.5455ZM29.0909 12.7273V20H25.4545V12.7273H29.0909ZM10.9091 21.8182H29.0909V23.6364C29.0909 26.0474 28.1331 28.3597 26.4282 30.0646C24.7234 31.7695 22.4111 32.7273 20 32.7273C17.5889 32.7273 15.2766 31.7695 13.5718 30.0646C11.8669 28.3597 10.9091 26.0474 10.9091 23.6364V21.8182ZM14.8545 25.4545C15.2302 26.5192 15.9268 27.441 16.8484 28.0931C17.7699 28.7452 18.8711 29.0954 20 29.0954C21.1289 29.0954 22.2301 28.7452 23.1516 28.0931C24.0732 27.441 24.7698 26.5192 25.1455 25.4545H14.8545Z"
                                fill="#FBFBFB"
                              />
                            </svg>
                          )}
                          <span className="text-surface font-bold text-2xl">
                            {score.toFixed(1)}
                          </span>
                        </div>
                      </div>
                    }
                  />
                );
              })}
              <div ref={ref} />
              {isFetchingNextPage && (
                <div className="w-max mx-auto py-4">
                  <Spinner size={32} />
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Modal de Filtro */}
      <Dialog open={isModalFilterOpen} onOpenChange={setIsModalFilterOpen}>
        <Dialog.Content className="max-w-[620px]">
          <Filters
            onFilter={() => {
              setIsModalFilterOpen(false);
            }}
          />
        </Dialog.Content>
      </Dialog>
    </div>
  );
}
